import { z } from "zod";
import {
  ROLES,
  REGIONS,
  CLINICAL_MODELS,
  SPECIALTIES,
  REPORT_TYPES,
  CONSENT_TYPES,
  VITAL_METRICS,
  VITAL_SOURCES,
} from "./types.js";

// --- Auth (reused) ---

export const signupSchema = z.object({
  // Clinic / organization name.
  companyName: z.string().min(2).max(120),
  name: z.string().min(2).max(120),
  email: z.string().email(),
  password: z.string().min(10).max(200),
  region: z.enum(REGIONS).default("us"),
  clinicalModel: z.enum(CLINICAL_MODELS).default("clinician_review"),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const passwordResetRequestSchema = z.object({
  email: z.string().email(),
});

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(10),
  password: z.string().min(10).max(200),
});

export const mfaCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
});

export const emailVerifyConfirmSchema = z.object({
  token: z.string().min(10),
});

// --- Team (staff: clinicians + admins) ---

export const inviteTeammateSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
  role: z.enum(ROLES).default("member"),
  isClinician: z.boolean().default(false),
  clinicianTitle: z.string().max(40).optional(),
  licenseNumber: z.string().max(80).optional(),
  licenseJurisdiction: z.string().max(40).optional(),
  npiNumber: z.string().max(20).optional(),
  message: z.string().max(1000).optional(),
});

export const updateTeammateSchema = z.object({
  role: z.enum(ROLES).optional(),
  isClinician: z.boolean().optional(),
  clinicianTitle: z.string().max(40).optional(),
  licenseNumber: z.string().max(80).optional(),
  licenseJurisdiction: z.string().max(40).optional(),
  npiNumber: z.string().max(20).optional(),
});

// --- Doctor persona (replaces interview template) ---

export const createPersonaSchema = z.object({
  name: z.string().min(2).max(160),
  specialty: z.enum(SPECIALTIES).default("general_medicine"),
  language: z.string().default("English"),
  defaultDurationMinutes: z.number().int().min(5).max(60).default(20),
  greeting: z.string().max(2000).optional(),
  personaInstructions: z.string().max(8000).optional(),
  scopeNotes: z.string().max(2000).optional(),
  tavusReplicaId: z.string().optional(),
  tavusPersonaId: z.string().optional(),
});

export const updatePersonaSchema = createPersonaSchema.partial();

// --- Patients ---

export const createPatientSchema = z.object({
  name: z.string().min(1).max(160),
  email: z.string().email(),
  dob: z.string().max(40).optional(), // encrypted at rest
  phone: z.string().max(40).optional(), // encrypted at rest
  sexAtBirth: z.enum(["female", "male", "intersex", "unknown"]).optional(),
  jurisdiction: z.string().max(40).optional(),
  timezone: z.string().max(64).optional(),
  allergies: z.array(z.string().min(1).max(120)).max(100).default([]),
  medications: z.array(z.string().min(1).max(120)).max(100).default([]),
  conditions: z.array(z.string().min(1).max(120)).max(100).default([]),
});

export const updatePatientSchema = createPatientSchema.partial();

// --- Consultations ---

export const bookConsultSchema = z.object({
  patientId: z.string().uuid(),
  personaId: z.string().uuid().optional(),
  clinicianId: z.string().uuid().optional(),
  reasonForVisit: z.string().max(2000).optional(),
  scheduledAt: z.string().datetime().optional(),
  timezone: z.string().max(64).optional(),
  durationMinutes: z.number().int().min(5).max(60).optional(),
});

// Patient intake submitted via the secure link before/at the call.
export const consultIntakeSchema = z.object({
  reasonForVisit: z.string().min(1).max(4000),
  symptoms: z.array(z.string().min(1).max(200)).max(50).default([]),
  timezone: z.string().max(64).optional(),
});

export const rescheduleConsultSchema = z.object({
  requestedAt: z.string().datetime(),
  timezone: z.string().max(64).optional(),
});

// --- Reports / ingestion ---

export const requestUploadSchema = z.object({
  patientId: z.string().uuid(),
  consultationId: z.string().uuid().optional(),
  type: z.enum(REPORT_TYPES).default("other"),
  fileName: z.string().min(1).max(300),
  mimeType: z.string().max(160).optional(),
  sizeBytes: z.number().int().min(1).max(100_000_000).optional(),
});

export const confirmUploadSchema = z.object({
  reportId: z.string().uuid(),
});

// --- Health journal ---

export const addJournalNoteSchema = z.object({
  patientId: z.string().uuid(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(8000),
});

// --- Vitals ---

export const recordVitalSchema = z.object({
  metric: z.enum(VITAL_METRICS),
  value: z.number(),
  unit: z.string().min(1).max(20),
  measuredAt: z.string().datetime().optional(),
  source: z.enum(VITAL_SOURCES).default("manual"),
});

// A batch of vitals streamed during a consult.
export const streamVitalsSchema = z.object({
  consultationId: z.string().uuid(),
  readings: z.array(recordVitalSchema).min(1).max(100),
});

// --- Consent ---

export const grantConsentSchema = z.object({
  consents: z.array(z.enum(CONSENT_TYPES)).min(1),
  version: z.string().max(20).default("v1"),
  timezone: z.string().max(64).optional(),
});

// --- Clinician review / sign-off ---

export const signOffSchema = z.object({
  decision: z.enum(["signed_off", "amended", "rejected"]),
  note: z.string().max(8000).optional(),
  // Optional clinician edits to the SOAP note before signing.
  soapNote: z
    .object({
      subjective: z.string().max(8000),
      objective: z.string().max(8000),
      assessment: z.string().max(8000),
      plan: z.string().max(8000),
    })
    .optional(),
});

export const addConsultNoteSchema = z.object({
  body: z.string().min(1).max(8000),
});

// --- Org profile / branding (reused) ---

const hexColor = z
  .string()
  .regex(/^#([0-9a-fA-F]{6})$/, "Use a hex color like #4f46e5")
  .or(z.literal(""))
  .optional();

// Clinic knowledge the AI doctor is briefed with. All optional free text.
export const clinicInfoSchema = z.object({
  hours: z.string().max(500).optional(),
  address: z.string().max(500).optional(),
  phone: z.string().max(60).optional(),
  website: z.string().max(300).optional(),
  services: z.string().max(2000).optional(),
  bookingInstructions: z.string().max(2000).optional(),
  timezone: z.string().max(60).optional(),
});
export type ClinicInfoInput = z.infer<typeof clinicInfoSchema>;

export const companyProfileSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  logoUrl: z.string().url().max(500).or(z.literal("")).optional(),
  brandColor: hexColor,
  accentColor: hexColor,
  tagline: z.string().max(140).or(z.literal("")).optional(),
  supportEmail: z.string().email().or(z.literal("")).optional(),
  region: z.enum(REGIONS).optional(),
  clinicalModel: z.enum(CLINICAL_MODELS).optional(),
  clinicInfo: clinicInfoSchema.optional(),
  schedulingType: z.enum(["builtin", "mcp"]).optional(),
});

// --- Follow-up appointments (built-in scheduling) ---
// Patient (or AI) requests a follow-up from the visit page.
export const requestFollowupSchema = z.object({
  reason: z.string().max(1000).optional(),
  preferredTime: z.string().max(300).optional(),
});
export type RequestFollowupInput = z.infer<typeof requestFollowupSchema>;

// Clinic staff update an appointment (schedule, assign, annotate).
export const updateAppointmentSchema = z.object({
  status: z.enum(["requested", "scheduled", "completed", "cancelled"]).optional(),
  scheduledAt: z.string().datetime().or(z.literal("")).optional(),
  assignedClinicianId: z.string().uuid().or(z.literal("")).optional(),
  notes: z.string().max(2000).optional(),
});
export type UpdateAppointmentInput = z.infer<typeof updateAppointmentSchema>;

// --- Clinic knowledge documents (RAG) ---
export const addClinicDocSchema = z.object({
  title: z.string().min(1).max(200),
  content: z.string().min(1).max(100000),
  sourceType: z.enum(["text", "file"]).optional(),
});
export type AddClinicDocInput = z.infer<typeof addClinicDocSchema>;

// --- Clinical orders (prescriptions + lab orders from a consult) ---
export const createOrderSchema = z.object({
  kind: z.enum(["prescription", "lab"]),
  title: z.string().min(1).max(300),
  details: z.record(z.unknown()).optional(),
  status: z.enum(["draft", "issued", "cancelled"]).optional(),
  aiGenerated: z.boolean().optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

// --- Pricing / platform settings (reused) ---

export const pricingRatesSchema = z.object({
  tavusPerMinuteUsd: z.number().min(0),
  summaryInputUsdPerMTok: z.number().min(0),
  summaryOutputUsdPerMTok: z.number().min(0),
  ingestionCostUsd: z.number().min(0),
  emailsPerConsult: z.number().min(0),
  emailUsdPer1000: z.number().min(0),
  routerbenchMarkup: z.number().min(1),
  profitMultiplier: z.number().min(1),
  roundUpToCents: z.number().int().min(1),
});

export const platformSettingsSchema = z.object({
  tavusApiKey: z.string().optional(),
  tavusDefaultReplicaId: z.string().optional(),
  routerbenchApiKey: z.string().optional(),
  summaryModel: z.string().optional(),
  triageModel: z.string().optional(),
  visionModel: z.string().optional(),
  extractionModel: z.string().optional(),
  embeddingModel: z.string().optional(),
  resendApiKey: z.string().optional(),
  resendFrom: z.string().optional(),
  stripeSecretKey: z.string().optional(),
  pricePerConsultCents: z.number().int().min(0).optional(),
  pricePerMinuteCents: z.number().int().min(0).optional(),
  pricing: pricingRatesSchema.partial().optional(),
});

// --- Inferred input types ---

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmSchema>;
export type MfaCodeInput = z.infer<typeof mfaCodeSchema>;
export type EmailVerifyConfirmInput = z.infer<typeof emailVerifyConfirmSchema>;
export type InviteTeammateInput = z.infer<typeof inviteTeammateSchema>;
export type UpdateTeammateInput = z.infer<typeof updateTeammateSchema>;
export type CreatePersonaInput = z.infer<typeof createPersonaSchema>;
export type UpdatePersonaInput = z.infer<typeof updatePersonaSchema>;
export type CreatePatientInput = z.infer<typeof createPatientSchema>;
export type UpdatePatientInput = z.infer<typeof updatePatientSchema>;
export type BookConsultInput = z.infer<typeof bookConsultSchema>;
export type ConsultIntakeInput = z.infer<typeof consultIntakeSchema>;
export type RescheduleConsultInput = z.infer<typeof rescheduleConsultSchema>;
export type RequestUploadInput = z.infer<typeof requestUploadSchema>;
export type ConfirmUploadInput = z.infer<typeof confirmUploadSchema>;
export type AddJournalNoteInput = z.infer<typeof addJournalNoteSchema>;
export type RecordVitalInput = z.infer<typeof recordVitalSchema>;
export type StreamVitalsInput = z.infer<typeof streamVitalsSchema>;
export type GrantConsentInput = z.infer<typeof grantConsentSchema>;
export type SignOffInput = z.infer<typeof signOffSchema>;
export type AddConsultNoteInput = z.infer<typeof addConsultNoteSchema>;
export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;
export type PlatformSettingsInput = z.infer<typeof platformSettingsSchema>;
